/**
 * Waiter words for backend values. Presentation only: statuses and codes come from the backend; this file
 * decides how a waiter reads them. Raw backend messages are never shown.
 */
import { isApiError } from '@/api/errors';
import { formatLocation, isLocationType } from '@/venue/venue';
import type { LocationType } from '@/venue/venue';

export function locationText(type: string, label: string): string {
  return isLocationType(type) ? formatLocation(type, label) : label;
}

export function locationTypeOf(type: string): LocationType {
  return isLocationType(type) ? type : 'TABLE';
}

/** "Guest" at a table, "Viewer" at a seat. */
export function guestWord(type: string): string {
  return locationTypeOf(type) === 'SEAT' ? 'Viewer' : 'Guest';
}

export function sessionStatusLabel(status: string): string {
  if (status === 'ACTIVE') return 'Open';
  const text = status.replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function formatDuration(minutes: number): string {
  if (minutes < 1) return 'Just opened';
  if (minutes < 60) return `${Math.floor(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.floor(minutes % 60);
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

const ASSISTANCE: Record<string, string> = {
  CALL_WAITER: 'Needs a waiter',
  WATER: 'Water',
  CUTLERY: 'Cutlery',
  BILL: 'Wants the bill',
  OTHER: 'Other request',
};

/** The neutral chip for help whose kind is not known: a request type this app has no words for. */
export const HELP_REQUESTED = 'Help requested';

/**
 * The customer's request in the same words the kitchen and the counter use. A type this app does not know
 * is never guessed at: it reads "Help requested".
 */
export function assistanceLabel(type: string): string {
  return ASSISTANCE[type] ?? HELP_REQUESTED;
}

/** "3 parties seated" in a restaurant, "2 seats in use" in a cinema. */
export function seatedText(count: number, cinema: boolean): string {
  if (cinema) return count === 1 ? '1 seat in use' : `${count} seats in use`;
  return count === 1 ? '1 party seated' : `${count} parties seated`;
}

/** "2 lines · 5 portions": what one order's serve action covers, counted from the lines the backend sent. */
export function portionsText(lines: number, portions: number): string {
  const l = lines === 1 ? '1 line' : `${lines} lines`;
  const p = portions === 1 ? '1 portion' : `${portions} portions`;
  return `${l} · ${p}`;
}

/** "5 min ago", from the request's own timestamp and the current time. */
export function waitedText(createdAt: string, now: number): string {
  const minutes = Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 60_000));
  return minutes < 1 ? 'Just now' : `${formatDuration(minutes)} ago`;
}

export function cartIssueLabel(issue: string | null, type: string): string | null {
  if (issue === 'ITEM_UNAVAILABLE') return 'No longer available';
  if (issue === 'INVALID_MODIFIERS')
    return `Choices need checking before the ${guestWord(type).toLowerCase()} orders`;
  return null;
}

/** "Serve" at a table, "Deliver" at a seat. */
export function serveWord(type: string): string {
  return locationTypeOf(type) === 'SEAT' ? 'Deliver' : 'Serve';
}

/** The serve button's label: "Mark served" at a table, "Mark delivered" at a seat. */
export function markServedLabel(type: string): string {
  return locationTypeOf(type) === 'SEAT' ? 'Mark delivered' : 'Mark served';
}

export type StaffAction = 'load' | 'add' | 'resolve' | 'open' | 'transfer' | 'serve';

/**
 * Plain words for a failed waiter action. Never a code, a raw message or a stack trace. A conflict is
 * explained as "the cart was updated", because that is what it means for the person holding the phone.
 */
export function describeStaffError(error: unknown, action: StaffAction): string {
  if (!isApiError(error)) return 'Something went wrong. Please try again.';
  if (error.isNetworkError) {
    if (action === 'load') return "Couldn't reach the server. Check the connection and try again.";
    if (action === 'add')
      return "Couldn't confirm whether the item was added. Check the cart before adding it again.";
    return "Couldn't confirm whether that went through. Check the screen before trying again.";
  }
  if (error.status === 401) return 'Your session has ended. Please sign in again.';
  if (error.status === 403) return "You're not allowed to do that.";

  if (error.hasCode('CART_REVISION_CONFLICT'))
    return "The customer's cart was updated while you were adding. The latest cart is shown. Check it, then add the item again if it's still needed.";
  if (error.hasCode('ITEM_OUT_OF_STOCK'))
    return 'That item is not available right now. The menu has been refreshed.';
  if (error.hasCode('SESSION_ALREADY_CLOSED'))
    return 'This session has already been closed. The floor has been refreshed.';
  if (error.hasCode('SESSION_NOT_FOUND')) {
    return action === 'transfer'
      ? "That session isn't at this location any more. The floor has been refreshed."
      : 'That session could not be found. It may have been closed.';
  }
  if (error.hasCode('ORDER_NOT_READY'))
    return 'That order is not ready yet. The list has been refreshed.';
  if (error.hasCode('ORDER_NOT_FOUND'))
    return 'That order could not be found. The list has been refreshed.';
  if (error.hasCode('ASSISTANCE_NOT_FOUND')) return 'That request could not be found.';
  if (error.hasCode('LOCATION_NOT_FOUND')) return 'That table or seat could not be found.';
  if (error.hasCode('TABLE_CAPACITY_EXCEEDED'))
    return "That table or seat doesn't have room for this party.";
  if (error.hasCode('LOCATION_OCCUPIED') || error.hasCode('LOCATION_UNAVAILABLE'))
    return "That table or seat can't take another session right now.";
  if (error.hasCode('DUPLICATE_IDEMPOTENCY_KEY'))
    return 'That request was already used for something different. Please try again.';
  if (error.status === 404) return 'That could not be found. The screen has been refreshed.';
  if (error.status === 400 || error.status === 422) {
    return action === 'add'
      ? 'Those choices are not valid for this item. Check the required choices and try again.'
      : 'Please check what you entered and try again.';
  }
  if (error.status === 409)
    return 'That changed while you were working. The screen has been refreshed.';
  if (error.status >= 500) return 'Something went wrong on our side. Try again in a moment.';
  return "That didn't go through. Please try again.";
}
